/**
 * CockpitSessionsView — the KI-Assistent's conversations on their own page:
 * the shared AssistantPage of @digitalisation/enlixy-comp (searchable list,
 * rename, delete, the open conversation in a wide column; below 1024 px the
 * list opens over the conversation) on the cockpit's chat API. `?s=<session>`
 * opens the conversation handed over from the drawer ("open as page").
 */

import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AssistantPage,
  useAssistant,
} from "@digitalisation/enlixy-comp/assistant";
import { useAssistantUi } from "../components/cockpit/assistant/assistantUiStore";
import {
  createCockpitBackend,
  type CockpitProposal,
} from "../components/cockpit/assistant/cockpitBackend";
import { ContextLabel } from "../components/cockpit/assistant/ContextLabel";
import { useCockpitConversation } from "../components/cockpit/assistant/conversation";
import { track } from "../help/learning";

const CockpitSessionsView: React.FC = () => {
  const [params] = useSearchParams();
  const backend = useMemo(() => createCockpitBackend(), []);
  const cp = useAssistant<CockpitProposal>(backend, {
    follow: false,
    initialSession: params.get("s") ?? undefined,
    onSend: () => track("assistant_asked"),
  });
  const conversation = useCockpitConversation();

  // The source dialog closes when another conversation is opened.
  const setSessionId = useAssistantUi((s) => s.setSessionId);
  useEffect(() => {
    setSessionId(cp.sessionId);
  }, [cp.sessionId, setSessionId]);
  useEffect(() => () => setSessionId(null), [setSessionId]);

  return (
    <main
      className="flex px-1 md:px-5"
      style={{
        height: "calc(100vh - var(--app-header-height, 80px) - 0.5rem)",
      }}
    >
      <AssistantPage
        cp={cp}
        contextLabel={<ContextLabel contextId={cp.contextId} />}
        conversation={conversation}
      />
    </main>
  );
};

export default CockpitSessionsView;
